import type { LaneId, VisibleCardViewV1, VisibleLaneV1 } from "@fate-kingdom/engine";

import { createPhysicalCardFaceSpec } from "./CardFaceTextureCache";
import { getFormationLayout } from "./formation-layout";
import { PhysicalCard3D } from "./PhysicalCard3D";

type FormationSide = VisibleLaneV1["own"] | VisibleLaneV1["opponent"];

export function LaneFormation3D({
  laneId,
  owner,
  side,
  position,
  selected,
  attackReady,
  reducedMotion,
  interactionBlocked,
  onSelectAttack,
  onInspect,
  hiddenCardInstanceId,
}: {
  readonly laneId: LaneId;
  readonly owner: "own" | "opponent";
  readonly side: FormationSide;
  readonly position: readonly [number, number, number];
  readonly selected: boolean;
  readonly attackReady: boolean;
  readonly reducedMotion: boolean;
  readonly interactionBlocked: boolean;
  readonly onSelectAttack?: ((laneId: LaneId) => void) | undefined;
  readonly onInspect: (cards: readonly VisibleCardViewV1[]) => void;
  readonly hiddenCardInstanceId?: string | undefined;
}) {
  const visibleCommander =
    side.commander?.cardInstanceId === hiddenCardInstanceId ? null : side.commander;
  const visibleArmies = side.armies.filter((army) => army.cardInstanceId !== hiddenCardInstanceId);
  const cards = [
    ...(visibleCommander === null ? [] : [visibleCommander]),
    ...visibleArmies,
  ] satisfies readonly VisibleCardViewV1[];
  const layout = getFormationLayout({
    owner,
    hasCommander: visibleCommander !== null,
    armyCount: visibleArmies.length,
  });
  const dimmed = side.status !== "active";
  const activateFormation = () => {
    if (interactionBlocked) return;
    if (attackReady && onSelectAttack !== undefined) onSelectAttack(laneId);
    else if (cards.length > 0) onInspect(cards);
  };

  return (
    <group position={[position[0], position[1], position[2]]}>
      {visibleArmies.map((army, index) => {
        const transform = layout.armies[index]!;
        return (
          <PhysicalCard3D
            face={createPhysicalCardFaceSpec(army, {
              subtitle: `Army ${String(index + 1)} · Size ${String(army.armySize)}`,
              stats: [`ATK ${String(army.attack)}`, `GRD ${String(army.guard)}`],
            })}
            position={transform.position}
            rotation={[-Math.PI / 2, 0, transform.rotationZ + (owner === "opponent" ? Math.PI : 0)]}
            scale={transform.scale}
            legal={attackReady}
            dimmed={dimmed}
            reducedMotion={reducedMotion}
            onActivate={activateFormation}
            sceneMetadata={{
              objectKind: "physical-card",
              cardInstanceId: army.cardInstanceId,
              cardDefinitionId: army.cardDefinitionId,
              cardType: army.type,
              ownerSide: owner,
              laneId,
              zone: "battlefield-army",
              visibleState: "face-up",
            }}
            key={army.cardInstanceId}
          />
        );
      })}
      {visibleCommander === null || layout.commander === null ? null : (
        <PhysicalCard3D
          face={createPhysicalCardFaceSpec(visibleCommander, {
            subtitle:
              side.formation?.preparing === true
                ? "Preparing"
                : side.formation?.attackedThisTurn === true
                  ? "Spent"
                  : "Commander · Ready",
            stats: [
              `ATK ${String(visibleCommander.attack ?? 0)}`,
              `GRD ${String(visibleCommander.guard ?? 0)}`,
            ],
          })}
          position={layout.commander.position}
          rotation={[-Math.PI / 2, 0, layout.commander.rotationZ]}
          scale={layout.commander.scale}
          selected={selected}
          legal={attackReady}
          dimmed={dimmed}
          reducedMotion={reducedMotion}
          onActivate={activateFormation}
          sceneMetadata={{
            objectKind: "physical-card",
            cardInstanceId: visibleCommander.cardInstanceId,
            cardDefinitionId: visibleCommander.cardDefinitionId,
            cardType: visibleCommander.type,
            ownerSide: owner,
            laneId,
            zone: "battlefield-commander",
            visibleState: "face-up",
          }}
        />
      )}
    </group>
  );
}
